import dayjs from 'dayjs';
import { startCase, capitalize } from 'lodash';
import { useShowSubscriber } from 'modules/for-verification/hooks';
import Button from 'partial/components/Button';
import AsyncButton from 'modules/common/components/AsyncButton';
import NavTab, { NavTabItem } from 'partial/components/NavTab';
import SwitchToggle from 'partial/components/SwitchToggle';
import ThumbIcon from 'partial/components/ThumbIcon';
import React from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import cn from 'classnames';
import { BsPencil } from 'react-icons/bs';
import { aclHelper } from 'helper';
import { useShowOrgWallet, useShowSubscriberSubscription } from '../hooks';
import { useOpenBanAccount } from '../modals/BanAccount';
import { useOpenBanExempt } from '../modals/BanExempt';
import Dashboard from './Dashboard';
import Integration from './Integration';
import MaskNames from './MaskNames';
import Messages from './Messages';
import History from './History';
import { useOpenEditRate } from '../modals/EditRate';
import Domain from './Domain';
import BanLogs from './BanLogs';

const tabRoutes = [
  {
    id: 1,
    to: 'dashboard',
    component: Dashboard,
  },
  {
    id: 2,
    to: 'mask-names',
    component: MaskNames,
  },
  {
    id: 3,
    to: 'integration',
    component: Integration,
  },
  {
    id: 4,
    to: 'messages',
    component: Messages,
  },
  {
    id: 5,
    to: 'history',
    component: History,
  },
  {
    id: 6,
    to: 'domain',
    component: Domain,
  },
  {
    id: 7,
    to: 'ban-logs',
    component: BanLogs,
  },
];

function SubscriberProfile() {
  const { id } = useParams();
  const openEditRate = useOpenEditRate();
  const [isSubLoading, subscriber] = useShowSubscriber(id);
  const [isPlanLoading, subscription] = useShowSubscriberSubscription(id);
  const org = subscriber?.organization;
  const orgName = org?.name;
  const [isWalletLoading, wallet] = useShowOrgWallet(org?.uuid);
  const openBanExempt = useOpenBanExempt();
  const openBanAccount = useOpenBanAccount();
  return (
    <div className="flex-1 flex flex-col px-6 gap-y-6 overflow-x-auto">
      <div className="px-6 py-3 rounded-sm shadow">
        {isSubLoading || isPlanLoading || isWalletLoading ? (
          <div className="animate-pulse flex space-x-4">
            <div className="rounded-full bg-gray-50 h-24 w-24" />
            <div className="flex-1 space-y-6 py-1">
              <div className="h-8 bg-gray-50 rounded" />
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-8 bg-gray-50 rounded col-span-2" />
                  <div className="h-8 bg-gray-50 rounded col-span-1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-8 bg-gray-50 rounded col-span-2" />
                  <div className="h-8 bg-gray-50 rounded col-span-1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-8 bg-gray-50 rounded col-span-2" />
                  <div className="h-8 bg-gray-50 rounded col-span-1" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex gap-4 xl:gap-10">
            <ThumbIcon
              className="h-20 w-20 rounded-full"
              initialsClassName="text-4xl"
              alt={orgName}
              url={subscriber?.profile?.avatar || ''}
            />
            <div className="flex flex-col flex-1">
              <div className="pb-4 flex justify-between items-start border-b border-gray-200/50">
                <div className="space-y-1 xl:space-y-0">
                  <h3 className="xl:hidden font-semibold text-gray-700 tracking-wider">
                    {orgName}
                  </h3>
                  <div className="hidden xl:flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-700 tracking-wider">
                      {orgName}
                    </h3>

                    {subscriber && (
                      <SwitchToggle
                        value={!subscriber?.organization?.is_banned}
                        label={
                          subscriber?.organization?.is_banned
                            ? 'Banned'
                            : 'Active'
                        }
                      />
                    )}
                  </div>
                  {subscription && (
                    <>
                      <div className="font-[500] text-sm text-gray-800 tracking-wide">
                        {capitalize(subscription?.name)} Account
                      </div>
                      <div className="flex gap-2 text-sm items-center mt-1">
                        <span className="font-light text-gray-700/60">
                          Rate:
                        </span>
                        <span className="text-gray-800 font-[500]">
                          {org?.rate_per_sms}
                        </span>
                        {aclHelper(
                          'subscriber_subscription',
                          'u',
                          <AsyncButton
                            onClick={() => openEditRate(org)}
                            color="link"
                          >
                            <div className="flex items-center gap-1 !capitalize">
                              <BsPencil />
                              <span>edit</span>
                            </div>
                          </AsyncButton>,
                        )}
                      </div>
                    </>
                  )}

                  <div className="xl:hidden w-min">
                    {subscriber && (
                      <SwitchToggle
                        value={!subscriber?.organization?.is_banned}
                        label={
                          subscriber?.organization?.is_banned
                            ? 'Banned'
                            : 'Active'
                        }
                      />
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-bold text-gray-800">
                    {wallet ? wallet?.balance : '--'}
                  </h2>
                  <div className=" font-light text-gray-700/60 ">
                    Credit Load
                  </div>
                </div>
              </div>
              <div className="flex justify-between flex-col xl:flex-row gap-3 py-3">
                <div className="flex-grow grid grid-cols-2 gap-3 ">
                  <div className="flex flex-col 2xl:flex-row 2xl:gap-1">
                    <h4 className="text-[14px] font-light text-gray-700/60 tracking-wide">
                      Contact Person:
                    </h4>
                    <p className="text-[14px] text-gray-700/90 tracking-wide">
                      {startCase(
                        capitalize(
                          `${subscriber?.profile?.first_name || ''} ${
                            subscriber?.profile?.last_name || ''
                          }`,
                        ),
                      )}
                    </p>
                  </div>

                  <div className="flex flex-col 2xl:flex-row 2xl:gap-1">
                    <h4 className="text-[14px] font-light text-gray-700/60 tracking-wide">
                      Mobile Number:
                    </h4>
                    <p className="text-[14px] text-gray-700/90 tracking-wide">
                      {subscriber?.profile?.mobile_number}
                    </p>
                  </div>

                  <div className="flex flex-col 2xl:flex-row 2xl:gap-1">
                    <h4 className="text-[14px] font-light text-gray-700/60 tracking-wide whitespace-nowrap">
                      Email Address:
                    </h4>
                    <p className="text-[14px] text-gray-700/90 tracking-wide break-all">
                      {subscriber?.email}
                    </p>
                  </div>

                  <div className="flex flex-col 2xl:flex-row 2xl:gap-1">
                    <h4 className="text-[14px] font-light text-gray-700/60 tracking-wide">
                      Registration Date:
                    </h4>
                    <p className="text-[14px] text-gray-700/90 tracking-wide">
                      {dayjs(subscriber?.profile?.created_at).format(
                        'MMM DD, YYYY',
                      )}
                    </p>
                  </div>
                </div>
                {aclHelper(
                  'subscriber_organization',
                  'u',
                  <div className="w-full xl:w-auto self-start grid grid-cols-2 xl:grid-cols-1 gap-3 xl:gap-2">
                    <Button
                      {...(org?.is_ban_exempted
                        ? { success: true }
                        : { primary: true })}
                      className="uppercase h-7 w-[160px] !rounded-[0.26rem]"
                      onClick={() => openBanExempt(subscriber)}
                    >
                      {org?.is_ban_exempted ? 'exempted' : 'ban exempt'}
                    </Button>
                    <Button
                      {...(org?.is_banned
                        ? { danger: true }
                        : { secondary: true })}
                      className={cn(
                        'uppercase h-7 w-[160px] !rounded-[0.26rem] transition',
                        org?.is_ban_exempted ? 'opacity-0' : 'opacity-100',
                      )}
                      onClick={() => openBanAccount(subscriber)}
                    >
                      {org?.is_banned ? 'unban account' : 'ban account'}
                    </Button>
                  </div>,
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 mt-2 flex flex-col gap-4">
        <NavTab>
          {tabRoutes.map(({ id: identifier, to }) => (
            <NavTabItem key={identifier} to={to} label={startCase(to)} />
          ))}
        </NavTab>
        <div className="flex-1 h-full w-full overflow-auto">
          <div className="h-full border border-gray-100/50 px-4 pt-4 pb-2 rounded shadow">
            <Routes>
              {tabRoutes.map(({ id: identifier, to, component: Component }) => (
                <Route key={identifier} path={to} element={<Component />} />
              ))}
              <Route index element={<Navigate replace to="dashboard" />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriberProfile;
